<template>
  <div :class="classes">
    <button
      v-for="segment in segments"
      :key="segment.label"
      :aria-label="segment.ariaLabel || segment.label"
      :class="segment.selected ? 'segmented-button__segment--selected': ''"
      :tabindex="segment.selected ? '-1' : null"
      type="button"
      class="segmented-button__segment"
      data-test-id="segmented-button-segment"
      @click="segment.onClick"
    >
      {{ segment.label }}
    </button>
  </div>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

const segmentsValidator = (segments) => Array.isArray(segments) &&
  segments.length > 1 && segments.length <= 3;

const sizes = ['s', 'm', 'l'];

export default {
  name: 'ESegmentedButton',
  mixins: [classes],
  props: {
    /**
     * an array of objects, where each object has a `label`.
     * If an `onClick` function is specified, this will be attached as a click handler.
     * If an `ariaLabel` is specified, this will be used as `aria-label` instead of the `label`.
     */
    segments: {
      type: Array,
      validator: segmentsValidator,
      default: () => {},
    },
    size: {
      type: String,
      default: 'm',
      validator: (size) => sizes.includes(size),
      cssClass: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.segmented-button {
  border-radius: $border-radius-full;
  border: solid $border-width var(--color-form-border-default);
  color: var(--color-button-content-secondary);
  display: inline-grid;
  font-weight: $font-weight-l;
  gap: $space-2s + $border-width;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  padding: $space-2s / 2;
}

.segmented-button__segment {
  appearance: none;
  background-color: transparent;
  color: var(--color-button-content-secondary);
  border: none;
  cursor: pointer;
  padding: 0 $space-m;
  position: relative;
  transition: background-color $timing-2s $easing;

  & + &::before {
    content: '';
    display: block;
    position: absolute;
    background-color: var(--color-form-border-default);
    height: 100%;
    width: $border-width;
    top: 0;
    left: -1 * ($space-2s / 2 + $border-width);
  }

  &:first-child {
    border-top-left-radius: $border-radius-full;
    border-bottom-left-radius: $border-radius-full;
  }

  &:last-child {
    border-top-right-radius: $border-radius-full;
    border-bottom-right-radius: $border-radius-full;
  }

  &:hover {
    background-color: var(--color-button-background-transparent-hover);
  }

  &:active {
    background-color: var(--color-button-background-transparent-active);
  }

  &:focus {
    @extend %keyboard-nav-focus;
  }

  &.segmented-button__segment:focus {
    outline-offset: 0;
  }

  .segmented-button--size-s & {
    height: $space-2l;
    font-size: $font-m;
  }

  .segmented-button--size-m & {
    height: $space-3l;
    font-size: $font-l;
  }

  .segmented-button--size-l & {
    height: $space-4l;
    font-size: $font-1l;
  }
}

.segmented-button__segment--selected {
  background: var(--color-highlighter);
  text-decoration: underline;
  pointer-events: none;

  &:hover {
    background: var(--color-highlighter);
  }
}
</style>
